import { Role } from "@/features/dashboard/routes/sidebar.config";
import type { User } from "@/store/auth.store";

export const ONBOARDING_COMPLETED_STEP = "COMPLETED";

export type AppShell = "owner" | "web" | "agent";

/** Agents complete onboarding via invite join, not contractor onboarding wizard. */
export function isAgentUser(user: User | null | undefined): boolean {
  return user?.role === Role.AGENT;
}

export function getUserAppShell(user: User | null | undefined): AppShell {
  if (!user) return "agent";
  if (user.role === Role.CONTRACTOR) return "owner";
  if (user.appShell === "web" || user.appShell === "agent" || user.appShell === "owner") {
    return user.appShell;
  }
  return "agent";
}

export function canAccessDashboard(user: User | null): boolean {
  if (!user) return false;
  if (isAgentUser(user)) {
    return true;
  }
  if (user.isOnboardingComplete !== true) return false;
  if (user.onboardingStep !== ONBOARDING_COMPLETED_STEP) return false;
  if (user.role === Role.ON_BOARDING) return false;
  return true;
}

/** Admins and invited agents skip the private beta wall. */
export function isBetaGateExempt(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.role === Role.ADMIN) return true;
  if (isAgentUser(user)) return true;
  return false;
}

/** Completed onboarding but not yet approved for private beta. */
export function needsBetaGate(user: User | null | undefined): boolean {
  if (!user) return false;
  if (!canAccessDashboard(user)) return false;
  if (isBetaGateExempt(user)) return false;
  return user.isBetaUser !== true;
}

export function isBetaAccessPath(pathname: string): boolean {
  return pathname === "/dashboard/beta-access";
}

export function getDefaultDashboardPath(user: User | null | undefined): string {
  if (!user) return "/dashboard";
  if (user.role === Role.AGENT) {
    return getUserAppShell(user) === "web" ? "/dashboard" : "/dashboard/agent";
  }
  return "/dashboard";
}

/** After login / onboarding: onboarding → beta wall → real dashboard. */
export function getPostAuthDestination(user: User | null | undefined): string {
  if (!user) return "/login";
  if (!canAccessDashboard(user)) return "/onboarding";
  if (needsBetaGate(user)) return "/dashboard/beta-access";
  return getDefaultDashboardPath(user);
}

/** Contractor account id — org owner id for team members, self for contractors. */
export function getOrgContractorId(user: User | null | undefined): string {
  if (!user) return "";
  if (user.role === Role.CONTRACTOR) return user.id;
  if (user.role === Role.AGENT && user.organizationId) {
    return user.organizationId;
  }
  return user.id;
}

export function isWebShellAgent(user: User | null | undefined): boolean {
  return user?.role === Role.AGENT && getUserAppShell(user) === "web";
}

/** Resources behind contractor-org pages (team, roles, job feed). */
export type ContractorOrgResource = "TEAMS" | "ROLES" | "BIDS";

/**
 * Contractor-org pages are backed by contractor-only API routes, so property
 * owners and admins must not render them — the requests would only 403.
 */
export function canAccessContractorOrgPath(
  user: User | null | undefined,
  resource: ContractorOrgResource,
): boolean {
  if (!user) return false;
  if (user.role === Role.CONTRACTOR) return true;
  if (isWebShellAgent(user)) return hasPermission(user, resource, "read");
  return false;
}

/** Internal job-assignment inbox (not property-owner bid invitations). */
export function getDispatchesPath(user: User | null | undefined): string {
  return getUserAppShell(user) === "web"
    ? "/dashboard/dispatches"
    : "/dashboard/agent/dispatches";
}

export function normalizeDispatchActionUrl(
  actionUrl: string | null | undefined,
  user: User | null | undefined,
): string | null {
  const trimmed = actionUrl?.trim();
  if (!trimmed) return null;
  if (
    user?.role === Role.AGENT &&
    getUserAppShell(user) === "web" &&
    trimmed === "/dashboard/agent/dispatches"
  ) {
    return "/dashboard/dispatches";
  }
  return trimmed;
}

export function isAgentOnlyPath(pathname: string): boolean {
  return pathname.startsWith("/dashboard/agent");
}

const WEB_AGENT_BLOCKED_PREFIXES = [
  "/dashboard/finance",
  "/dashboard/roles",
];

export function canAccessPath(user: User | null | undefined, pathname: string): boolean {
  if (!user) return false;
  const role = user.role;

  if (role === Role.AGENT) {
    const shell = getUserAppShell(user);
    if (shell === "web") {
      if (isAgentOnlyPath(pathname)) return false;
      if (
        WEB_AGENT_BLOCKED_PREFIXES.some((p) => pathname.startsWith(p)) &&
        !hasPermission(user, "FINANCIAL", "read") &&
        pathname.startsWith("/dashboard/finance")
      ) {
        return false;
      }
      if (
        pathname.startsWith("/dashboard/roles") &&
        !hasPermission(user, "ROLES", "read")
      ) {
        return false;
      }
      if (
        pathname.startsWith("/dashboard/team") &&
        !hasPermission(user, "TEAMS", "read")
      ) {
        return false;
      }
      if (
        pathname.startsWith("/dashboard/bids") &&
        !hasPermission(user, "BIDS", "read")
      ) {
        return false;
      }
      return (
        pathname.startsWith("/dashboard") ||
        pathname.startsWith("/dashboard/settings") ||
        pathname.startsWith("/dashboard/profile") ||
        pathname === "/dashboard/customer-support"
      );
    }
    return (
      isAgentOnlyPath(pathname) ||
      pathname.startsWith("/dashboard/settings") ||
      pathname.startsWith("/dashboard/profile") ||
      pathname === "/dashboard/customer-support" ||
      pathname === "/dashboard"
    );
  }

  if (isAgentOnlyPath(pathname)) return false;
  return true;
}

export function hasPermission(
  user: User | null | undefined,
  resource: string,
  action: "read" | "create" | "update" | "delete",
): boolean {
  if (!user) return false;
  if (user.role === Role.CONTRACTOR) return true;
  const row = user.permissions?.[resource as keyof typeof user.permissions];
  if (!row) return false;
  switch (action) {
    case "create":
      return row.canCreate;
    case "read":
      return row.canRead;
    case "update":
      return row.canUpdate;
    case "delete":
      return row.canDelete;
    default:
      return false;
  }
}
